import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Sparkles, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [providers, setProviders] = useState({ google: false, facebook: false, microsoft: false });

  useEffect(() => {
    api.get("/auth/providers").then((r) => setProviders(r.data)).catch(() => {});
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    const res = await register(email, password, name);
    setBusy(false);
    if (res.ok) {
      toast.success("Account created. Welcome to DmFlio!");
      nav("/app");
    } else {
      setErr(res.error);
    }
  };

  const social = async (provider) => {
    try {
      const { data } = await api.get(`/auth/${provider}/start`);
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="min-h-screen bg-background grid-noise flex items-center justify-center p-4">
      <Link to="/" data-testid="back-home" className="absolute top-6 left-6 flex items-center gap-2 font-bold hover:underline">
        <ArrowLeft className="w-4 h-4" /> Home
      </Link>

      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-11 h-11 bg-primary border-2 border-foreground rounded-md shadow-brutal flex items-center justify-center">
              <Sparkles className="w-6 h-6" strokeWidth={2.5} />
            </div>
            <span className="font-heading font-extrabold text-2xl">DMFlow</span>
          </div>
          <h1 className="text-3xl font-extrabold">Start automating.</h1>
          <p className="text-sm text-muted-foreground mt-1 font-medium">40 DMs / month, free forever.</p>
        </div>

        <form onSubmit={onSubmit} className="brutal bg-white p-6 space-y-4">
          {(providers.google || providers.facebook || providers.microsoft) && (
            <div className="space-y-2">
              {providers.google && (
                <button type="button" onClick={() => social("google")} data-testid="register-google" className="w-full flex items-center justify-center gap-2 bg-white border-2 border-foreground rounded-md py-2.5 font-bold text-sm shadow-brutal-sm hover:shadow-brutal hover:-translate-y-0.5 transition-all">
                  <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                  Sign up with Google
                </button>
              )}
              {providers.microsoft && (
                <button type="button" onClick={() => social("microsoft")} data-testid="register-microsoft" className="w-full flex items-center justify-center gap-2 bg-white border-2 border-foreground rounded-md py-2.5 font-bold text-sm shadow-brutal-sm hover:shadow-brutal hover:-translate-y-0.5 transition-all">
                  <svg width="16" height="16" viewBox="0 0 23 23"><path fill="#F25022" d="M0 0h11v11H0z"/><path fill="#7FBA00" d="M12 0h11v11H12z"/><path fill="#00A4EF" d="M0 12h11v11H0z"/><path fill="#FFB900" d="M12 12h11v11H12z"/></svg>
                  Sign up with Microsoft
                </button>
              )}
              {providers.facebook && (
                <button type="button" onClick={() => social("facebook")} data-testid="register-facebook" className="w-full flex items-center justify-center gap-2 bg-[#1877F2] text-white border-2 border-foreground rounded-md py-2.5 font-bold text-sm shadow-brutal-sm hover:shadow-brutal hover:-translate-y-0.5 transition-all">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  Sign up with Facebook
                </button>
              )}
              <div className="relative my-3">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t-2 border-foreground"></div></div>
                <div className="relative flex justify-center"><span className="bg-white px-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">or with email</span></div>
              </div>
            </div>
          )}

          <div>
            <label className="text-xs font-bold uppercase tracking-wider mb-1.5 block">Your name</label>
            <input required value={name} onChange={(e) => setName(e.target.value)} data-testid="register-name" className="brutal-input w-full" placeholder="Alex Creator" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider mb-1.5 block">Email</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="register-email" className="brutal-input w-full" placeholder="you@example.com" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider mb-1.5 block">Password</label>
            <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="register-password" className="brutal-input w-full" placeholder="At least 6 characters" />
          </div>
          {err && <div data-testid="register-error" className="text-sm font-semibold text-destructive bg-destructive/10 border-2 border-destructive p-2 rounded-md">{err}</div>}
          <button type="submit" disabled={busy} data-testid="register-submit" className="w-full bg-primary border-2 border-foreground rounded-md py-3 font-bold shadow-brutal-sm hover:shadow-brutal hover:-translate-y-0.5 transition-all disabled:opacity-50">
            {busy ? "Creating account…" : "Create my account"}
          </button>
          <div className="text-sm text-center font-medium pt-2">
            Already have an account? <Link to="/login" data-testid="goto-login" className="font-bold underline">Log in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
